/**
 * Message Bus implementation with shallow message history functionality
 */
export default class MessageBus {
  constructor() {
    this._logger = null;
    this._channels = new Map();
    this._nextMessageId = 0;
  }

  /**
   * Subscribe to a topic in a channel
   *
   * @param {string} channelId - Channel to which you want to subscribe
   * @param {(string|null)} topicId - Topic to which you want to subscribe (`null` to subscribe to all topics)
   * @param {function} handler
   */
  subscribe(channelId, topicId, handler) {
    const channel = this._getChannel(channelId);

    if (!topicId) {
      channel.globalHandlers.add(handler);

      this._deliverPreviousMessagesForChannel(channelId, handler);
    }

    const topic = this._getTopic(channelId, topicId);
    topic.handlers.add(handler);

    this._deliverPreviousMessagesForTopic(channelId, topicId, handler);
  }

  /**
   * Unsubscribe from a topic in a channel
   *
   * @param {string} channelId - Channel from which you want to unsubscribe
   * @param {(string|null)} topicId - Topic from which you want to unsubscribe (`null` to unsubscribe from all topics)
   * @param {function} handler
   */
  unsubscribe(channelId, topicId, handler) {
    const channel = this._getChannel(channelId);

    if (!topicId) {
      channel.globalHandlers.delete(handler);
      return;
    }

    const topic = this._getTopic(channelId, topicId);

    topic.handlers.delete(handler);
  }

  /**
   * Publish a message to a topic in a channel. Both IDs have to be specified.
   * Set persistent parameter for a message you want to get delivered to subscribers who'll subscribe after this
   * message was already published.
   *
   * @param {string} channelId - Channel to which you want to publish
   * @param {string} topicId - Topic to which you want to publish
   * @param {*} message - Message to be published
   * @param {boolean} persistent - True for message to be persistent and delivered to subscribers that subscribe later
   */
  publish(channelId, topicId, message = null, persistent = false) {
    const channel = this._getChannel(channelId);

    channel.globalHandlers.forEach(handler => {
      handler(message);
    });

    const topic = this._getTopic(channelId, topicId);

    topic.handlers.forEach(handler => {
      handler(message);
    });

    if (persistent) {
      topic.previousMessages.push({
        id: this._nextMessageId,
        timestamp: Date.now(),
        message,
      });
      this._nextMessageId += 1;
    }

    if (channelId !== 'log') {
      this._log(
        `MessageBus: Message published. Channel '${channelId}', topic '${topicId}', persistent: ${
          persistent ? 'yes' : 'no'
        }.`,
      );
    }
  }

  /**
   *
   * @param {string} channelId
   * @param {string} topicId
   */
  clearMessageHistory(channelId, topicId) {
    // For falsy channelId clear all channels
    if (!channelId) {
      this._channels.forEach(channel => {
        channel.topics.forEach(topic => {
          // Reassignment is the most efficient way to clear the array here
          // eslint-disable-next-line no-param-reassign
          topic.previousMessages = [];
        });
      });

      // Once all messages have been cleared it's safe to reset the counter
      this._nextMessageId = 0;

      this._log('MessageBus: Cleared messages from all channels.');
      return;
    }

    // For falsy topicId clear all topics in the channel
    if (!topicId) {
      this._getChannel(channelId).topics.forEach(topic => {
        // Reassignment is the most efficient way to clear the array here
        // eslint-disable-next-line no-param-reassign
        topic.previousMessages = [];
      });

      this._log(`MessageBus: Cleared messages from all topics in channel '${channelId}'.`);
      return;
    }

    // Clear specific topic in specific channel
    this._getTopic(channelId, topicId).previousMessages = [];

    this._log(`MessageBus: Cleared messages from topic '${topicId}' in channel '${channelId}'.`);
  }

  /**
   *
   * @param {string} channelId
   * @returns {object} channel
   * @private
   */
  _getChannel(channelId) {
    if (!this._channels.has(channelId)) {
      this._channels.set(channelId, {
        topics: new Map(),
        globalHandlers: new Set(),
      });
    }

    return this._channels.get(channelId);
  }

  /**
   *
   * @param {string} channelId
   * @param {string} topicId
   * @returns {object} topic
   * @private
   */
  _getTopic(channelId, topicId) {
    const channel = this._getChannel(channelId);

    if (!channel.topics.has(topicId)) {
      channel.topics.set(topicId, {
        handlers: new Set(),
        previousMessages: [],
      });
    }

    return channel.topics.get(topicId);
  }

  /**
   *
   * @param {string} topicId
   * @param {function} handler
   * @private
   */
  _deliverPreviousMessagesForTopic(channelId, topicId, handler) {
    this._getTopic(channelId, topicId).previousMessages.forEach(messageItem => {
      handler(messageItem.message, messageItem.timestamp);
    });
  }

  /**
   *
   * @param {string} channelId
   * @param {function} handler
   * @private
   */
  _deliverPreviousMessagesForChannel(channelId, handler) {
    const previousMessages = [];
    this._getChannel(channelId).topics.forEach(topic => {
      topic.previousMessages.forEach(messageItem => {
        previousMessages.push(messageItem);
      });
    });

    previousMessages.sort((a, b) => a.id - b.id);

    previousMessages.forEach(messageItem => {
      handler(messageItem.message, messageItem.timestamp);
    });
  }

  /**
   * To avoid cyclic dependency between the Logger and MessageBus, Logger instance attaches itself onto MessageBus
   * instance during initialization. This way we can use the single instance export pattern and still have both
   * instances invoking each other without a cyclic dependency.
   *
   * @param {Logger} logger
   */
  attachLogger(logger) {
    this._logger = logger;
  }

  /**
   * Since standard logging practice is to publish log messages using MessageBus, MessageBus itself logs by directly
   * calling Logger methods to avoid unnecessary self-invocation.
   *
   * @param {string} message
   * @private
   */
  _log(message) {
    if (!this._logger) {
      return;
    }

    this._logger.debug(message, 'message-bus');
  }
}
